import React, { useEffect, useMemo, useRef, useState } from 'react';

import {
  DEFAULT_CLASS,
  DISABLED_CLASS,
  ERROR_CLASS,
} from '@/components/form/constants';
import { InputEvent, InputProps } from '@/components/form/Input';
import { Link } from '@/components/Link';
import { Popover } from '@/components/Popover';

export interface Option {
  value: number | string;
  label: string;
  disabled?: boolean;
  divider?: boolean;
}

export interface SelectProps {
  options?: Option[];
}

export const Select = (props: InputProps & SelectProps) => {
  const {
    className,
    error,
    placeholder,
    disabled,
    options: optionsProps = [],
    value: defaultValue,
    onChange,
    name,
  } = props;

  // Memoize selected option calculation to prevent unnecessary recalculations
  const selectedOption = useMemo(
    () => optionsProps.find(option => option.value === defaultValue),
    [optionsProps, defaultValue]
  );

  const [options, setOptions] = useState<Option[]>(optionsProps);
  const [value, setValue] = useState(selectedOption?.label || defaultValue);
  const [selected, setSelected] = useState<Option | undefined>(selectedOption);
  const [isOpen, setIsOpen] = useState(false);
  const [hoverIndex, setHoverIndex] = useState(0);

  // Sync internal state when props change
  // IMPORTANT: This useEffect does NOT call onChange - that should only happen on user interaction
  useEffect(() => {
    const newSelectedOption = optionsProps.find(
      option => option.value === defaultValue
    );
    setSelected(newSelectedOption);
    setValue(newSelectedOption?.label || defaultValue);
    setOptions(optionsProps);
  }, [optionsProps, defaultValue]);

  // Reset hover index when options change
  useEffect(() => {
    setHoverIndex(0);
  }, [options]);

  function handleKeyEvent(event: React.KeyboardEvent<HTMLInputElement>) {
    const key = event.key;
    if (key === 'Enter') {
      if (isOpen && hoverIndex !== -1) {
        setSelected(options[hoverIndex]);
      }
      setIsOpen(!isOpen);
    } else if (key === 'ArrowUp') {
      setHoverIndex(prev => (prev === 0 ? options.length - 1 : prev - 1));
    } else if (key === 'ArrowDown') {
      setHoverIndex(prev => (prev === options.length - 1 ? 0 : prev + 1));
    } else if (key === 'Escape') {
      setIsOpen(false);
    }
  }

  function handleSelect(option: Option) {
    setSelected(option);
    setValue(option.label);
    setOptions(optionsProps);
    setIsOpen(false);
    // Only call onChange on actual user selection
    onChange({
      target: {
        value: String(option.value),
        name,
      },
    } as InputEvent);
  }

  function handleChange(event: InputEvent) {
    const value = event.target.value;
    const filteredOptions = optionsProps.filter(option =>
      option.label.toLowerCase().includes(value.toLowerCase())
    );
    setOptions(filteredOptions);
    setValue(value);
    onChange(event);
  }

  return (
    <>
      <Popover
        placement="bottom"
        className="border-chariot-stroke border-2 p-0"
        matchContainerWidth
        open={isOpen}
        setOpen={setIsOpen}
        container={
          <div className="relative">
            <input
              name={name}
              id={name}
              className={`border-chariot-stroke bg-chariot-background-primary block w-full cursor-pointer rounded-[2px] border p-1.5 pr-8 pl-3 ${DISABLED_CLASS} ${error ? ERROR_CLASS : DEFAULT_CLASS} ${className}`}
              placeholder={placeholder}
              value={value}
              disabled={disabled}
              aria-invalid={!!error}
              aria-describedby={error ? `${name}-error` : name}
              onClick={() => setIsOpen(!isOpen)}
              onKeyUp={event => handleKeyEvent(event)}
              tabIndex={0}
              onChange={handleChange}
            />
          </div>
        }
      >
        <ul
          className="bg-chariot-background-primary max-h-60 w-full overflow-auto rounded-[2px] py-1 text-base shadow-lg"
          aria-orientation="vertical"
          id="select-options"
          tabIndex={-1}
          role="listbox"
        >
          {options.map(({ label, value }, index) => {
            const isSelected = selected?.value === value;
            const isHovered = hoverIndex === index;

            return options[index].divider ? (
              <div
                key={`divider-${index}`}
                className="border-chariot-stroke my-1 w-full border-t"
              />
            ) : (
              <SelectItem
                key={value}
                label={label}
                value={value}
                isSelected={isSelected}
                isHovered={isHovered}
                index={index}
                handleSelect={handleSelect}
                setHoverIndex={setHoverIndex}
                disabled={options[index].disabled}
              />
            );
          })}
        </ul>
      </Popover>
    </>
  );
};

interface SelectItemProps {
  label: string;
  value: number | string;
  isSelected: boolean;
  isHovered: boolean;
  index: number;
  handleSelect: (option: Option) => void;
  setHoverIndex: (index: number) => void;
  disabled?: boolean;
}

const SelectItem = (props: SelectItemProps) => {
  const {
    label,
    value,
    isSelected,
    isHovered,
    index,
    handleSelect,
    setHoverIndex,
    disabled,
  } = props;
  const ref = useRef<HTMLLIElement>(null);

  const isLink = typeof value === 'string' && value.startsWith('http');

  if (isLink) {
    return (
      <li className="-ml-1">
        <Link to={value as string} className="block truncate">
          {label}
        </Link>
      </li>
    );
  }
  return (
    <li
      className={`text-chariot-text-primary relative cursor-default px-3 py-2 select-none ${isHovered ? 'bg-chariot-background-secondary cursor-pointer' : ''} ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
      role="menuitem"
      aria-haspopup="true"
      tabIndex={0}
      key={value}
      aria-selected={isSelected}
      data-hovered={isHovered}
      onMouseDown={event => {
        event.stopPropagation();
        !disabled && handleSelect({ label, value });
      }}
      onMouseOver={() => !disabled && setHoverIndex(index)}
      ref={ref}
    >
      <span className={`${isSelected ? 'font-semibold' : ''} block truncate`}>
        {label}
      </span>
    </li>
  );
};
